import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ChatConversationSummaryDto,
  ChatMessageFeedbackDto,
  CreateConversationRequestDto,
  SetMessageFeedbackRequestDto,
} from 'src/api/FiretrolApiClient';
import { chatbotApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { rollbackList } from 'src/api/queries/optimistic';
import {
  IChatUiMessage,
  toConversationSummary,
  toUiMessages,
} from 'src/api/queries/chat/useChatConversationQueries';

export function useCreateChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateConversationRequestDto) =>
      chatbotApiClient.createConversation(request),
    meta: { suppressErrorToast: true, errorContext: 'create conversation' },
    onSettled: () => invalidateFor('chat.conversation.write', qc),
  });
}

export function useDeleteChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => chatbotApiClient.deleteConversation(id),
    meta: { suppressErrorToast: true, errorContext: 'delete conversation' },
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: queryKeys.chat.messages(id) });
    },
    onSettled: () => invalidateFor('chat.conversation.write', qc),
  });
}

export interface RenameChatConversationArgs {
  id: string;
  title: string;
}

export function useRenameChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: RenameChatConversationArgs) =>
      chatbotApiClient.renameConversation(id, { title }),
    meta: { errorContext: 'rename conversation' },
    onMutate: async ({ id, title }: RenameChatConversationArgs) => {
      const queryKey = queryKeys.chat.conversations();
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ChatConversationSummaryDto[]>(queryKey);
      qc.setQueryData<ChatConversationSummaryDto[]>(queryKey, (existing) =>
        (existing ?? []).map((c) => (c.id === id ? { ...c, title } : c)),
      );
      return { previous, queryKey };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.queryKey, ctx.previous);
    },
    onSettled: () => invalidateFor('chat.conversation.write', qc),
  });
}

export interface IForkChatConversationArgs {
  conversationId: string;
  messageId: string;
}

export function useForkChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, messageId }: IForkChatConversationArgs) =>
      chatbotApiClient.forkConversation(conversationId, { messageId }),
    meta: { suppressErrorToast: true, errorContext: 'fork conversation' },
    onSuccess: (forked) => {
      qc.setQueryData<IChatUiMessage[]>(
        queryKeys.chat.messages(forked.id),
        toUiMessages(forked),
      );
      qc.setQueryData<ChatConversationSummaryDto>(
        queryKeys.chat.conversation(forked.id),
        toConversationSummary(forked),
      );
      // The chat header and forked-from line read the active conversation
      // from this list, so it must hold the fork before the refetch lands.
      qc.setQueryData<ChatConversationSummaryDto[]>(
        queryKeys.chat.conversations(),
        (existing) =>
          existing && [
            toConversationSummary(forked),
            ...existing.filter((c) => c.id !== forked.id),
          ],
      );
    },
    onSettled: () => invalidateFor('chat.conversation.write', qc),
  });
}

export interface IChatMessageFeedbackTarget {
  conversationId: string;
  messageId: string;
}

export interface ISetChatMessageFeedbackArgs
  extends IChatMessageFeedbackTarget {
  request: SetMessageFeedbackRequestDto;
}

const patchCachedFeedback = async (
  qc: QueryClient,
  { conversationId, messageId }: IChatMessageFeedbackTarget,
  feedback: ChatMessageFeedbackDto | undefined,
) => {
  const queryKey = queryKeys.chat.messages(conversationId);
  await qc.cancelQueries({ queryKey });
  const previous = qc.getQueryData<IChatUiMessage[]>(queryKey);
  qc.setQueryData<IChatUiMessage[]>(queryKey, (existing) =>
    existing?.map((message) =>
      message.id === messageId ? { ...message, feedback } : message,
    ),
  );
  return { previous, queryKey };
};

export function useSetChatMessageFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, request }: ISetChatMessageFeedbackArgs) =>
      chatbotApiClient.setMessageFeedback(messageId, request),
    meta: { errorContext: 'save feedback' },
    onMutate: (args: ISetChatMessageFeedbackArgs) =>
      patchCachedFeedback(qc, args, args.request),
    onError: (_err, _args, ctx) => rollbackList(qc, ctx),
    onSettled: () => invalidateFor('chat.messageFeedback.write', qc),
  });
}

export function useDeleteChatMessageFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId }: IChatMessageFeedbackTarget) =>
      chatbotApiClient.deleteMessageFeedback(messageId),
    meta: { errorContext: 'remove feedback' },
    onMutate: (args: IChatMessageFeedbackTarget) =>
      patchCachedFeedback(qc, args, undefined),
    onError: (_err, _args, ctx) => rollbackList(qc, ctx),
    onSettled: () => invalidateFor('chat.messageFeedback.write', qc),
  });
}
